import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchProcessedReferralData } from '../../services/leadManagementApi';
import axios from 'axios';
import { withRetry } from '../../lib/utils/withRetry';
import { withLmaSafeApiLogging } from '../../lib/utils/lmaSafeLogging';
import { LMADropdownResponse } from '../../components/LMA/types';

const objectToQueryString = (obj: any) => {
  const keys = Object.keys(obj);
  const keyValuePairs = keys.map((key) => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
  });
  return keyValuePairs.join('&');
};

export interface ProcessedReferralsState {
  data: any[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  currentPage: number;
  pageSize: number;
  filters: Record<string, string>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dropdownData: LMADropdownResponse | null;
  dropdownLoading: boolean;
  dropdownError: string | null;
  /** Processed tab: true = profile API (full-profile rows); false = enrollment API */
  useFullProfileProcessedData: boolean;
}

/** Supports store key used in app (`lmaProcessedReferrals`) and slice tests (`processedReferrals`). */
function selectProcessedReferralsBranch(state: unknown): ProcessedReferralsState {
  const r = state as Record<string, ProcessedReferralsState>;
  return r.lmaProcessedReferrals ?? r.processedReferrals;
}

/** Ensures table columns get ids from full-profile rows when root fields are missing. */
function normalizeProcessedReferralItem(item: any): any {
  let row = item;
  if (!item.deduplication && item.deduplicationJson) {
    try {
      const parsed = JSON.parse(item.deduplicationJson);
      row = { ...item, deduplication: Array.isArray(parsed) ? parsed : [] };
    } catch {
      row = item;
    }
  }
  const details = row.leadDetails;
  if (Array.isArray(details) && details.length > 0) {
    const first = details[0];
    return {
      ...row,
      id:
        row.id ??
        String(row.patientId ?? row.patientCCId ?? first.patientId ?? first.enrollmentId ?? ''),
      guestRef: row.guestRef || first.guestRef,
      enrollmentId: row.enrollmentId || first.enrollmentId,
      patientId: row.patientId || first.patientId,
      leadId: row.leadId ?? first.leadId,
    };
  }
  return row;
}

const initialState: ProcessedReferralsState = {
  data: [],
  totalRecords: 0,
  loading: false,
  error: null,
  lastUpdated: null,
  currentPage: 1,
  pageSize: 10,
  filters: {},
  sortBy: undefined,
  sortOrder: undefined,
  dropdownData: null,
  dropdownLoading: false,
  dropdownError: null,
  useFullProfileProcessedData: true,
};

// Async thunk for fetching processed referrals data with filters and pagination
export const fetchProcessedReferrals = createAsyncThunk(
  'processedReferrals/fetchProcessedReferrals',
  async (data: any, { rejectWithValue, dispatch, getState }) => {
    try {
      const branch = selectProcessedReferralsBranch(getState());
      const useFullProfile = branch.useFullProfileProcessedData;
      const response = await fetchProcessedReferralData(data.params, data.payload, useFullProfile);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load processed data. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching dropdown data for processed referrals tab
export const fetchProcessedReferralsDropdownData = createAsyncThunk(
  'processedReferrals/fetchDropdownData',
  async (leadStatusFilters: any[], { rejectWithValue, dispatch }) => {
    try {
      let hotLeadMin = 0;
      let coldLeadMax = 0;

      if (leadStatusFilters && Array.isArray(leadStatusFilters)) {
        leadStatusFilters.forEach((filter: any) => {
          const name = filter.name?.toLowerCase();
          const lowerLimit = filter.fields?.LSFDaysLowerLimit?.value;
          const upperLimit = filter.fields?.LSFDaysUpperLimit?.value;

          if (name && lowerLimit !== undefined) {
            if (name === 'hot' && upperLimit !== undefined) {
              hotLeadMin = lowerLimit;
            } else if (name === 'cold') {
              coldLeadMax = upperLimit;
            }
          }
        });
      }

      const params = {
        LeadStatus: 'processed',
        LeadType: 'referral',
        HotLeadMin: hotLeadMin,
        ColdLeadMax: coldLeadMax,
      };

      const response = await withLmaSafeApiLogging(
        {
          operation: 'fetchProcessedReferralsDropdownData',
          method: 'GET',
          endpoint: '/LMA/GetLMAFilterDropdownValues',
        },
        () =>
          withRetry(() =>
            axios.get(
              `/api/dap-api?url=/LMA/GetLMAFilterDropdownValues&${objectToQueryString(params)}`
            )
          )
      );
      return response?.data as LMADropdownResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load filter options. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

const processedReferralsSlice = createSlice({
  name: 'processedReferrals',
  initialState,
  reducers: {
    setUseFullProfileProcessedData: (state, action: PayloadAction<boolean>) => {
      state.useFullProfileProcessedData = action.payload;
    },
    updateProcessedReferralRecord: (state, action: PayloadAction<{ id: string; updates: any }>) => {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...action.payload.updates };
      }
    },
    deleteProcessedReferralRecord: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter((item) => item.id !== action.payload);
    },
    addProcessedReferralRecord: (state, action: PayloadAction<any>) => {
      state.data.push(action.payload);
    },
    setFilters: (state, action: PayloadAction<Record<string, string>>) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to page 1 when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to page 1 when page size changes
    },
    setSorting: (state, action: PayloadAction<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    clearProcessedReferralsData: (state) => {
      state.data = [];
      state.totalRecords = 0;
      state.error = null;
      state.lastUpdated = null;
      state.filters = {};
      state.currentPage = 1;
      state.sortBy = undefined;
      state.sortOrder = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProcessedReferrals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProcessedReferrals.fulfilled, (state, action) => {
        state.loading = false;
        const rawData = action.payload.data || [];
        state.data = rawData.map((item: any) => normalizeProcessedReferralItem(item));
        state.totalRecords = action.payload.totalRecords || 0;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchProcessedReferrals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProcessedReferralsDropdownData.pending, (state) => {
        state.dropdownLoading = true;
        state.dropdownError = null;
      })
      .addCase(fetchProcessedReferralsDropdownData.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownData = action.payload;
      })
      .addCase(fetchProcessedReferralsDropdownData.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownError = action.payload as string;
      });
  },
});

export const {
  updateProcessedReferralRecord,
  deleteProcessedReferralRecord,
  addProcessedReferralRecord,
  setFilters,
  setPage,
  setPageSize,
  setSorting,
  clearProcessedReferralsData,
  setUseFullProfileProcessedData,
} = processedReferralsSlice.actions;

/** Switch Processed tab data source and reload current page with current filters/sort. */
export const setProcessedDataViewAndRefetch = createAsyncThunk(
  'processedReferrals/setDataViewAndRefetch',
  async (useFullProfile: boolean, { dispatch, getState }) => {
    dispatch(setUseFullProfileProcessedData(useFullProfile));
    const s = selectProcessedReferralsBranch(getState());
    const payload = {
      filters: s.filters,
      sortBy: s.sortBy,
      sortOrder: s.sortOrder,
    };
    const params = { PageNo: s.currentPage, PageSize: s.pageSize };
    await dispatch(fetchProcessedReferrals({ params, payload })).unwrap();
  }
);

export default processedReferralsSlice.reducer;
